import React from 'react';
import { graphql } from 'gatsby';
import { IFontIconCard } from '@ecs/tng-common/interfaces';
import StickyBannerDynamic from '../components/StickyBannerDynamic';
import LayoutFull from '../components/LayoutFull';
import LatestPostsComponent from '../components/latest-posts/LatestPosts';
import HeroSection from '../components/HeroSection';
import FeaturedPosts from '../components/FeaturedPosts';
import ActionCardsSlider from '../components/ActionCardsSlider';
import PopularPosts from '../components/PopularPosts';
import BlogCategoryLinks from '../components/BlogCategoryLinks';
import SEO from '../components/SEO';
import { exploreMoreLinks, heroSections } from '../content/blog-category-links';
import { actionCards, IActionCardsContent } from '../content/action-cards';
import { setDataSection, setEcsSiteSection } from '../helpers/analytics';
import { removeDuplicatedPosts, setSeoMetaTags } from '../helpers/functions';
import { categorySlugsWithScoreSearch } from '../helpers/data';
import '../styles/category-template.scss';
import { IPost, ISEO } from '../helpers/interfaces';
import CreditScoreSearch from '../components/CreditScoreSearch';

interface PageTemplateProps {
  pageContext: {
    catId: number;
    catSlug: string;
    currentPath: string;
    allIncludedCategories: number[];
    excludedArticles: number[];
    numberOfPages: number;
  };
  data: {
    wpCategory: {
      description: string;
      name: string;
      featuredPosts?: IPost[];
      popularPosts?: IPost[];
      expSeo: ISEO;
    };
    latestPosts: {
      nodes: IPost[];
    };
  };
}
//
export default function CategoryTemplate({ pageContext, data }: PageTemplateProps) {
  const { catSlug, currentPath, catId, allIncludedCategories, numberOfPages } = pageContext;
  const description = data?.wpCategory?.description;
  const featuredPosts = data?.wpCategory?.featuredPosts || [];
  const popularPosts = data?.wpCategory?.popularPosts || [];
  const latestPosts = data?.latestPosts?.nodes;
  const name = data?.wpCategory?.name || '';
  // Filter popularPosts to remove any duplicates from featuredPosts
  const featuredPostsSliced: IPost[] = featuredPosts?.slice(0, 9);
  const popularPostsFiltered =
    popularPosts.length > 0
      ? removeDuplicatedPosts(popularPosts, featuredPostsSliced)?.slice(0, 6)
      : [];

  const actionCardsData: IActionCardsContent = actionCards[catSlug];
  const dataForExploreMore: IFontIconCard[] = exploreMoreLinks[catSlug] || [];
  return (
    <LayoutFull>
      <HeroSection
        content={heroSections[catSlug] || []}
        heading={name}
        subheading={description}
        theme="dark"
        background="textured"
      />
      <StickyBannerDynamic
        category={catSlug}
        categoryId={catId}
      />
      {featuredPostsSliced.length > 0 ? (
        <FeaturedPosts
          theme={!actionCardsData && popularPostsFiltered?.length > 1 ? 'white' : 'coconut'}
          textured={false}
          toHideCategory={!(allIncludedCategories.length > 1)}
          heading="Editors' picks"
          content={featuredPostsSliced}
        />
      ) : null}
      {actionCardsData ? (
        <ActionCardsSlider
          catId={catId}
          content={actionCardsData}
          heading="Take action with these tools"
        />
      ) : null}
      {popularPostsFiltered?.length > 1 ? (
        <PopularPosts
          content={popularPostsFiltered}
          heading="What others are reading"
          theme="coconut"
          toHideCategory={!(allIncludedCategories.length > 1)}
          textured={false}
        />
      ) : null}

      {dataForExploreMore.length > 0 ? (
        <div
          className={`container container-py ${latestPosts?.length ? 'pb-5 pb-lg-7' : ''} ${popularPostsFiltered?.length < 2 ? 'border-top' : ''}`}>
          <BlogCategoryLinks links={dataForExploreMore} />
        </div>
      ) : null}
      {latestPosts?.length > 0 ? (
        <LatestPostsComponent
          catSlug={catSlug}
          toHideCategory={!(allIncludedCategories.length > 1)}
          numberOfPages={numberOfPages}
          currentPath={currentPath}
          initPosts={latestPosts}
          currentPage={1}
          className={`
            ${popularPosts.length < 2 && dataForExploreMore.length === 0 ? 'border-top' : ''} ${dataForExploreMore.length > 0 ? 'pt-0' : ''}
          `}
        />
      ) : null}
      {categorySlugsWithScoreSearch.includes(catSlug) ? (
        <div className="container container-py pt-0">
          <div className="row justify-content-center">
            <div className="col-md-10 col-lg-9 col-xl-8">
              <CreditScoreSearch variant="coconut" />
            </div>
          </div>
        </div>
      ) : null}
    </LayoutFull>
  );
}
export function Head({ pageContext, data }: PageTemplateProps) {
  const { catSlug, catId } = pageContext;
  const seo = data.wpCategory?.expSeo;
  const title = seo?.title || '';
  const description = seo?.description || '';
  const schema = seo?.schema || '';
  const schemaScript = schema && <script type="application/ld+json">{schema}</script>;
  const section = setDataSection(catSlug);
  const category = catSlug;
  return (
    <>
      <body
        data-section={section}
        data-category={category}
        data-id={catId.toString()}
        data-op="EXP-ASK-CAT"
        data-ecscategory={setEcsSiteSection('category', { category, section })}
      />
      <SEO
        title={title}
        description={description}
        noindex={seo?.metaRobotsNoindex !== 'index'}>
        {seo && setSeoMetaTags(seo)}
        {schemaScript}
      </SEO>
    </>
  );
}

export const query = graphql`
  query GetPostsPerCategory(
    $catId: Int!
    $limit: Int!
    $allIncludedCategories: [Int!]
    $excludedArticles: [Int!]
  ) {
    latestPosts: allWpPost(
      filter: {
        categories: { nodes: { elemMatch: { databaseId: { in: $allIncludedCategories } } } }
        databaseId: { nin: $excludedArticles }
      }
      sort: { date: DESC }
      limit: $limit
    ) {
      nodes {
        ...PostPreviewContent
        ...ByLineAuthor
        ...ByLineCategory
      }
    }
    wpCategory(databaseId: { eq: $catId }) {
      name
      popularPosts {
        ...PostPreviewContent
        ...ByLineAuthor
        ...ByLineCategory
      }
      featuredPosts {
        ...PostPreviewContent
        ...ByLineAuthor
        ...ByLineCategory
      }
      expSeo {
        ...ExpSEOFields
      }
      description
    }
  }
`;
